import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateLineChannel } from 'api/line';
import { queryKeys } from 'constants/queryKeys';
import { lineStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type { TLineChannelOperationResponse, TUpdateLineChannelRequest } from 'types';
import { toErrorMessage } from 'hooks/utils';

type UseUpdateLineChannelOptions = {
  onSuccess?: (response: TLineChannelOperationResponse) => void;
};

type UseUpdateLineChannelResult = {
  update: (lineChannelId: number, request: TUpdateLineChannelRequest) => void;
  isSubmitting: boolean;
  error: string | undefined;
};

export function useUpdateLineChannel(
  options: UseUpdateLineChannelOptions = {},
): UseUpdateLineChannelResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const strings = lineStrings.edit;

  const [error, setError] = useState<string | undefined>();

  const mutation = useMutation({
    mutationFn: async (params: {
      lineChannelId: number;
      request: TUpdateLineChannelRequest;
    }) => {
      if (!user) {
        throw new Error(strings.errors.unauthorized);
      }
      return updateLineChannel(params.lineChannelId, params.request);
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.LINE_CHANNEL_ACCOUNTS, user?.uid],
      });
      queryClient.invalidateQueries({
        queryKey: [
          queryKeys.LINE_CHANNEL_DETAIL,
          variables.lineChannelId,
          user?.uid,
        ],
      });
      setError(undefined);
      options.onSuccess?.(response);
    },
    onError: (caught) => {
      setError(toErrorMessage(caught, strings.errors.updateFailed));
    },
  });

  const update = (
    lineChannelId: number,
    request: TUpdateLineChannelRequest,
  ) => {
    setError(undefined);
    mutation.mutate({ lineChannelId, request });
  };

  return {
    update,
    isSubmitting: mutation.isPending,
    error,
  };
}
